import { map, distinctUntilChanged } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AnyType } from '@src/models/common';
import type { BaseSchema } from 'yup';
import * as yup from 'yup';
import { ERROR_MESSAGES } from '@src/consts/errors';
import { IEntityValidators } from '@src/models/entity';
import { TLegoV2Element } from './models';

export const getSubscription = (obs: Observable<AnyType>, elementId: string, cb: (v: AnyType) => void) => {
  return obs
    .pipe(
      map((v) => v[elementId]),
      distinctUntilChanged(),
    )
    .subscribe(cb);
};

const buildYup = (validators: IEntityValidators): BaseSchema => {
  switch (validators.dataType) {
    case 'BOOLEAN':
      // Булен не может быть null или undefined.
      // Закладываем это на уровне платформы.
      // Если это надо, то стоит исопользовать другой управляющий элемент
      return yup.boolean().default(false);
    case 'NUMBER':
      const numValidator = yup
        .number()
        .typeError(ERROR_MESSAGES.NUMBER)
        .nullable(true)
        .transform((_, originValue) => {
          if (!originValue && originValue !== 0) return null;

          return +originValue.toString().replaceAll(',', '.');
        });

      if (validators.required) numValidator.nullable(false);

      // validators.integer - значение может только целочисленным
      if (validators.integer) {
        return numValidator.integer(ERROR_MESSAGES.NUMBER_INT);
      }

      // validators.decimal - значение может быть как целочисленным, так и дробным (максимально 2 цифры после запятой)
      if (validators.decimal) {
        return numValidator.test('is-decimal', ERROR_MESSAGES.NUMBER_DECIMAL, (value) => {
          if (value) return /^-?\d+(\.\d{0,2})?$/.test(value.toString());
          return true;
        });
      }
      return numValidator;
    case 'STRING':
      let ret = validators.required ? yup.string() : yup.string().nullable(true);
      // Пришлось написать кастомный валидатор, чтобы уйти от конфликта проверки на пустоту и минимальной длины
      if (validators.min) {
        ret = ret.test('min', ERROR_MESSAGES.STRING_MIN.replace('%s', validators.min.toString()), (value) => {
          if (!value) {
            return true;
          }
          return value.length >= Number(validators.min);
        });
      }
      if (validators.max) {
        ret = ret.max(validators.max, ERROR_MESSAGES.STRING_MAX.replace('%s', validators.max.toString()));
      }
      if (validators.email) {
        ret = ret.email(ERROR_MESSAGES.EMAIL);
      }
      if (validators.phone) {
        /** TODO: Добавить валидацию для разных форматов телефонов */
        ret = ret.test({
          test: (value) => {
            if (!value) return true;
            const clearedPhone = value.replace(/ |_+/g, '').trim();
            return clearedPhone.length >= 11;
          },
          message: ERROR_MESSAGES.PHONE,
        });
      }
      return ret;
    case 'STRUCT':
      return yup.mixed();
  }
};

export const getElementYupValidator = (validators: IEntityValidators): BaseSchema | undefined => {
  if (validators) {
    let validatorElem = buildYup(validators);
    if (!validatorElem) {
      console.error('Скорее всего в валидаторе элемента на прописан dataType');
    }

    if (validators.required) {
      validatorElem = validatorElem.required(ERROR_MESSAGES.REQUIRED);
    }
    if (validators.repeated) {
      // @link https://github.com/jquense/yup#array
      validatorElem = validators.required
        ? yup.array().of(validatorElem).min(1, ERROR_MESSAGES.REQUIRED).required(ERROR_MESSAGES.REQUIRED)
        : yup.array().of(validatorElem);
    }

    if (Array.isArray(validators.custom) && validators.custom.length > 0) {
      for (const config of validators.custom) {
        validatorElem = validatorElem.test(config);
      }
    }
    return validatorElem;
  }
  return undefined;
};

export const getCommonElementYupValidator = (elems: TLegoV2Element[]) => {
  const schemaObj = {} as { [key: string]: BaseSchema };

  elems.forEach((e) => {
    if (e.validators) {
      const scheme = getElementYupValidator(e.validators);
      if (scheme) {
        schemaObj[e.id] = scheme;
      }
    }
  });

  return yup.object().shape(schemaObj);
};
export const getErrorsMessage = (e: AnyType) => {
  if (Array.isArray(e)) {
    return e.join(', ');
  }

  if (typeof e === 'string') return e;

  return undefined;
};
